import _ from 'lodash';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import styled from 'styled-components';

import {
  Col,
  InputNumber,
  List,
  Row,
  Select,
  Skeleton,
  Typography,
} from 'antd';

import { convertToMonetary } from 'shared/utils';

const { Text } = Typography;

const Wrapper = styled.div`
  border: solid 1px #ddd;
  padding: 20px;
`;

const PurchaseOrdersCostSummary = (props) => {
  const {
    childRef,
    lineItems = [],
    tax = { type: 'percentage', value: 0 },
    onChangeTaxType,
    onChangeTaxValue,
  } = props;

  const store = useRef(
    makeAutoObservable({
      _lineItems: lineItems,
      _tax: tax,
      get tax() {
        return this._tax;
      },
      set tax(value) {
        this._tax = value;
      },
      get lineItems() {
        return this._lineItems;
      },
      set lineItems(value) {
        this._lineItems = value;
      },
      get summaryItems() {
        return [
          {
            description: 'Parts',
            amount: _.chain(this.lineItems)
              .filter((lineItem) => lineItem.type === 'part')
              .sumBy((lineItem) => {
                return (lineItem.unitCost || 0) * (lineItem.quantity || 0);
              })
              .value(),
          },
          {
            description: 'Services',
            amount: _.chain(this.lineItems)
              .filter((lineItem) => lineItem.type === 'service')
              .sumBy((lineItem) => {
                return (lineItem.unitCost || 0) * (lineItem.quantity || 0);
              })
              .value(),
          },
          {
            description: 'Subtotal',
            amount: _.chain(this.lineItems)
              .sumBy((lineItem) => {
                return (lineItem.unitCost || 0) * (lineItem.quantity || 0);
              })
              .value(),
          },
          {
            description: 'Tax',
            amount: this.tax.value,
            amountType: this.tax.type || 'percentage',
          },
        ];
      },
      get total() {
        const costsTotal = _.sumBy(this.lineItems, (i) => {
          return (i.unitCost || 0) * (i.quantity || 0);
        });
        const taxValue =
          this.tax.type === 'percentage'
            ? (this.tax.value / 100) * costsTotal
            : this.tax.value
              ? parseFloat(this.tax.value)
              : 0;
        const total = _.sum([costsTotal, taxValue]);
        return total;
      },
      toPercentage(value = 0) {
        return value.toLocaleString('en-US', {
          style: 'percent',
        });
      },
    })
  ).current;

  useEffect(() => {
    runInAction(() => {
      store.lineItems = lineItems;
      store.tax = tax;
    });
  }, [lineItems, tax]);

  useEffect(() => {
    if (childRef) {
      childRef(store);
    }
  }, [childRef]);

  const handleChangeTaxType = (value) => {
    if (onChangeTaxType) {
      onChangeTaxType(value);
    }
  };

  const handleChangeTaxValue = (value) => {
    if (onChangeTaxValue) {
      onChangeTaxValue(value);
    }
  };

  const renderExtraFields = (item) => {
    if (item.description === 'Tax') {
      return (
        <>
          <Select
            defaultValue={store.tax.type || 'percentage'}
            onChange={handleChangeTaxType}
            options={[
              { value: 'percentage', label: '%' },
              { value: 'absolute', label: '$' },
            ]}
            style={{ width: '60px', marginLeft: '30px' }}
          />
          <InputNumber
            defaultValue={store.tax.value || 0}
            onChange={handleChangeTaxValue}
            formatter={(value) =>
              `${store.tax.type === 'absolute' ? '$ ' : ''}${value}`.replace(
                /\B(?=(\d{3})+(?!\d))/g,
                ','
              )
            }
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            style={{ width: '70px', marginRight: '10px', marginLeft: '5px' }}
            data-testid="purchase-order-tax-input"
          />
        </>
      );
    }
    return null;
  };

  const renderCurrencyOrPct = (item) => {
    if (item.amountType && item.amountType === 'percentage') {
      return (
        <div data-testid={`cost-summary-${item.description.toLowerCase()}`}>
          {store.toPercentage(item.amount / 100)}
        </div>
      );
    }
    return (
      <div data-testid={`cost-summary-${item.description.toLowerCase()}`}>
        {convertToMonetary(item.amount)}
      </div>
    );
  };

  return (
    <Wrapper>
      <Row gutter={[8, 8]}>
        <Col span={24}>
          <List
            header={<Text strong>Cost Summary</Text>}
            footer={
              <List.Item>
                <List.Item.Meta title={<Text strong>Total</Text>} />
                <div data-testid="cost-summary-total">
                  {convertToMonetary(store.total)}
                </div>
              </List.Item>
            }
            dataSource={store.summaryItems}
            renderItem={(item) => (
              <List.Item>
                <Skeleton title={false} loading={item.loading} active>
                  <List.Item.Meta
                    title={item.description}
                    style={{ whiteSpace: 'nowrap' }}
                  />
                  {renderExtraFields(item)}
                  {renderCurrencyOrPct(item)}
                </Skeleton>
              </List.Item>
            )}
          />
        </Col>
      </Row>
    </Wrapper>
  );
};

PurchaseOrdersCostSummary.propTypes = {
  childRef: PropTypes.func,
  lineItems: PropTypes.array,
  tax: PropTypes.object,
  onChangeTaxType: PropTypes.func,
  onChangeTaxValue: PropTypes.func,
};

export default observer(PurchaseOrdersCostSummary);
